import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import {
  type BalanceReferenceDto,
  type CaseReferenceMintDto,
  getCaseReferenceFromConnect,
  mintCasePartialReferenceOnConnect,
} from '@/lib/connect-api';
import { getMockCaseReference } from '@/lib/mockData';

/** `loading` and `unavailable` are the only states in which no data exists, and neither
 *  carries any — so "a code is absent" cannot be confused with "we do not know yet", and
 *  no caller can read a reference out of a state that has none. */
type CaseReferenceData = {
  reference: BalanceReferenceDto | null;
  partialReferences: BalanceReferenceDto[];
  /** The case's payable balance. `null` means the case is not payable at all (closed, a
   *  credit case, or covered by a plan) — distinct from a balance of zero. */
  payableAmountSubunits: number | null;
};

type CaseReferenceStatus =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | ({ status: 'ready' } & CaseReferenceData);

export type CaseReferenceRead = CaseReferenceStatus & {
  /** The `(account, case)` pair this read is bound to. Callers key components holding their
   *  own reference state on it, so that state cannot outlive the subject — the portal can
   *  swap account under a mounted tree when another tab signs in, and a code kept across
   *  that would show one customer's payable reference to another. */
  scopeKey: string | null;
  /** Re-read after a mint, for durability across a reload. Never the only thing standing
   *  between a minted code and the screen — see the mint callers. */
  refetch: () => void;
  /** The live custom-amount code for exactly this amount, or null. Amounts match in
   *  subunits so a typed "50.00" and "50" resolve to one code. */
  partialFor: (amountSubunits: number) => BalanceReferenceDto | null;
  mintPartial: (amountSubunits: number) => Promise<CaseReferenceMintDto>;
};

const NO_DATA: CaseReferenceData = {
  reference: null,
  partialReferences: [],
  payableAmountSubunits: null,
};

type Internal = CaseReferenceStatus & { readFor: string | null };

/**
 * Reads one case's live payment codes and binds its mint verbs. Reading never mints.
 *
 * Every failure — feature off (404), a case the caller does not own, no account context,
 * network error — collapses to `unavailable`, so callers render nothing rather than surface
 * an error the customer cannot act on. The three 404 reasons are indistinguishable by
 * design; do not try to tell them apart here.
 *
 * The mint verbs are returned bound to the same `(account, case)` key the read is guarded
 * on, so the write path cannot be pointed at a different subject than the read.
 *
 * Call once per screen and pass the result down: two components each calling it would issue
 * two reads per render, and remount on every tab switch.
 */
export function useCaseReference(caseId: string | null | undefined): CaseReferenceRead {
  const { accountId, authStage, getAccessToken } = useAuth();
  const { useMockData } = useConfiguration();
  const [state, setState] = useState<Internal>({ status: 'loading', readFor: null });
  const [reloads, setReloads] = useState(0);
  const refetch = useCallback(() => setReloads((n) => n + 1), []);

  // Called, never read from — so its identity must not decide when we re-read. A context
  // that rebuilds it each render (this one memoises today; nothing makes it) would otherwise
  // turn every render into a fresh Connect read, and every read into another render.
  const tokenRef = useRef(getAccessToken);
  tokenRef.current = getAccessToken;

  // The pair, not the case alone: the portal can swap account under a mounted tree (another
  // tab signs in with a different access code) while `authStage` and the routed caseId both
  // stay put.
  const key = caseId && accountId ? `${accountId}:${caseId}` : null;

  useEffect(() => {
    const ready = (d: {
      reference: BalanceReferenceDto | null;
      partial_references?: BalanceReferenceDto[];
      payable_amount_subunits: number | null;
    }): Internal => ({
      status: 'ready',
      reference: d.reference,
      partialReferences: d.partial_references ?? [],
      payableAmountSubunits: d.payable_amount_subunits,
      readFor: key,
    });

    if (useMockData && caseId) {
      setState(ready(getMockCaseReference(caseId)));
      return;
    }
    if (!key || !caseId || (authStage !== 'limited' && authStage !== 'full')) {
      setState({ status: 'unavailable', readFor: key });
      return;
    }
    let active = true;
    setState({ status: 'loading', readFor: key });
    getCaseReferenceFromConnect(caseId, authStage, tokenRef.current)
      .then((r) => active && setState(ready(r)))
      .catch(() => active && setState({ status: 'unavailable', readFor: key }));
    return () => {
      active = false;
    };
  }, [useMockData, key, caseId, authStage, reloads]);

  // Guard at render, not only in the effect: effects run after paint, so an account or case
  // switch would otherwise show the previous one's code for a frame. The amount is bound to
  // that case's balance and the code cannot be retracted, so one frame is enough to send a
  // customer to pay the wrong debt.
  const current: CaseReferenceStatus =
    state.readFor === key ? state : { status: 'loading' };

  const data = current.status === 'ready' ? current : NO_DATA;

  const partialFor = useCallback(
    (amountSubunits: number) =>
      data.partialReferences.find((r) => r.amount_subunits === amountSubunits) ??
      // A partial request for exactly the balance is served the pay-in-full product, recorded
      // under a different kind — so it never appears among the partials, and without this the
      // tab re-offers the action for an amount that already holds a live code.
      (data.reference?.amount_subunits === amountSubunits ? data.reference : null),
    [data.partialReferences, data.reference],
  );

  const mintPartial = useCallback(
    (amountSubunits: number) =>
      mintCasePartialReferenceOnConnect(caseId ?? '', amountSubunits, authStage, tokenRef.current),
    [caseId, authStage],
  );

  return useMemo(
    () => ({ ...current, scopeKey: key, refetch, partialFor, mintPartial }),
    [current, key, refetch, partialFor, mintPartial],
  );
}
